import React from 'react';
import { Box, IconButton, ListItemButton, Tooltip, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { useAtom } from 'jotai';
import { AppContext } from '../../App';
import { favoriteOrgsAtom } from '../../atoms/org-atoms';
import { IOrg } from '../../types/my-types';
import { useNavigate } from 'react-router';

interface OrgSidebarItemProps {
  org: IOrg;
  isCollapsed: boolean;
}

function OrgSidebarItem({ org, isCollapsed }: OrgSidebarItemProps) {
  const navigate = useNavigate();
  const { setOrgLabel, setOrgId, orgId, setOrg } = React.useContext(AppContext);
  const isSelected = orgId === org.id;
  const itemRef = React.useRef<HTMLDivElement>(null);
  const [favoriteOrgs, setFavoriteOrgs] = useAtom(favoriteOrgsAtom);
  const isFavorite = favoriteOrgs.includes(org.id);

  React.useEffect(() => {
    if (isSelected && itemRef.current) {
      const list = itemRef.current.closest('[data-cy="org-list"]');
      if (list) {
        const listRect = list.getBoundingClientRect();
        const itemRect = itemRef.current.getBoundingClientRect();
        // Only scroll if the item is not fully visible within the list
        const isAbove = itemRect.top < listRect.top;
        const isBelow = itemRect.bottom > listRect.bottom;
        if (isAbove || isBelow) {
          itemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  }, [isSelected]);

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFavoriteOrgs((prev) =>
      prev.includes(org.id) ? prev.filter((id) => id !== org.id) : [...prev, org.id]
    );
  };

  const handleClick = () => {
    setOrgId(org.id);
    setOrgLabel(org.label);
    setOrg(org);
    navigate(`/organization/${org.id}`);
  };

  if (isCollapsed) {
    return (
      <Tooltip title={org.label} placement="right" arrow>
        <ListItemButton
          ref={itemRef}
          data-cy="org-list-item"
          selected={isSelected}
          onClick={handleClick}
          sx={{
            justifyContent: 'center',
            px: '7px',
            py: '7px',
            borderRadius: '8px',
            mb: 0.5,
            position: 'relative',
            bgcolor: isSelected ? 'rgba(29,154,170,0.08)' : 'transparent',
            '&:hover': { bgcolor: '#F1F6F8' },
            '&.Mui-selected': {
              bgcolor: 'rgba(29,154,170,0.08)',
              '&:hover': { bgcolor: 'rgba(29,154,170,0.12)' },
            },
            '&.Mui-selected::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 4,
              bottom: 4,
              width: 3,
              borderRadius: '0 3px 3px 0',
              bgcolor: '#1D9AAA',
            },
          }}
        >
          <div>
            <Typography
              sx={{
                fontSize: '13.5px',
                fontWeight: isSelected ? 600 : 500,
                color: isSelected ? '#187787' : '#1a2332',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {org.label.slice(0, 2).toUpperCase()}
            </Typography>
          </div>
        </ListItemButton>
      </Tooltip>
    );
  }

  return (
    <ListItemButton
      ref={itemRef}
      data-cy="org-list-item"
      selected={isSelected}
      onClick={handleClick}
      sx={{
        px: 1.5,
        py: '10px',
        borderRadius: '8px',
        mb: 0.5,
        position: 'relative',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        bgcolor: isSelected ? 'rgba(29,154,170,0.08)' : 'transparent',
        '&:hover': { bgcolor: '#F1F6F8' },
        '&.Mui-selected': {
          bgcolor: 'rgba(29,154,170,0.08)',
          '&:hover': { bgcolor: 'rgba(29,154,170,0.12)' },
        },
        '&.Mui-selected::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: '0 3px 3px 0',
          bgcolor: '#1D9AAA',
        },
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '13.5px',
              fontWeight: isSelected ? 600 : 500,
              color: isSelected ? '#187787' : '#1a2332',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {org.label}
          </Typography>
        </Box>
        {org.is_inactive && (
          <Tooltip
            title="Inactive orgs are deleted on the 15th of each month. To keep an organization active, set one of its datapool's status to active."
            placement="top"
            arrow
          >
            <Box
              component="span"
              sx={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#F44336',
                bgcolor: 'rgba(244,67,54,0.08)',
                px: '7px',
                py: '2px',
                borderRadius: '4px',
                flexShrink: 0,
              }}
            >
              Inactive
            </Box>
          </Tooltip>
        )}
        <IconButton
          data-cy="org-favorite-toggle"
          size="small"
          onClick={toggleFavorite}
          sx={{
            p: '2px',
            flexShrink: 0,
            color: isFavorite ? '#F5A623' : '#BDC3D2',
            opacity: isFavorite ? 1 : 0,
            transition: 'opacity 0.15s, color 0.15s',
            '.MuiListItemButton-root:hover &': { opacity: 1 },
            '&:hover': { color: isFavorite ? '#E09500' : '#8E99A8' },
          }}
        >
          {isFavorite ? <StarIcon sx={{ fontSize: 18 }} /> : <StarOutlineIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </div>
    </ListItemButton>
  );
}

export default OrgSidebarItem;
